import { useEffect, useState } from 'react';
import { api } from '../../api';
import useOtpResendCooldown, { OTP_RESEND_COOLDOWN_SEC } from '../../hooks/useOtpResendCooldown';
import { ProfileChangeContact } from './ProfileChangePanel';

export default function ProfileContactChange({
  type,
  mode,
  title,
  initialValue = '',
  token,
  onDone,
  t,
}) {
  const [step, setStep] = useState('input');
  const [value, setValue] = useState(initialValue || '');
  const [code, setCode] = useState('');
  const [challenge, setChallenge] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const { secondsLeft, canResend, startCooldown, resetCooldown } = useOtpResendCooldown(OTP_RESEND_COOLDOWN_SEC);

  useEffect(() => {
    setStep('input');
    setValue(initialValue || '');
    setCode('');
    setChallenge(null);
    setErr('');
    setMsg('');
    resetCooldown();
  }, [type, mode, initialValue, resetCooldown]);

  const isPhone = type === 'phone';
  const inputMessage = isPhone
    ? (mode === 'verify'
      ? t('account.profile.otp.verifyPhoneHint')
      : mode === 'add'
        ? t('account.profile.otp.addPhoneHint')
        : t('account.profile.otp.changePhoneHint'))
    : (mode === 'verify'
      ? t('account.profile.otp.verifyEmailHint')
      : mode === 'add'
        ? t('account.profile.otp.addEmailHint')
        : t('account.profile.otp.changeEmailHint'));

  const codeMessage = isPhone
    ? t('account.profile.otp.codeSentPhone')
    : t('account.profile.otp.codeSentEmail');

  const sendCode = async (e) => {
    e?.preventDefault?.();
    if (step === 'code' && !canResend) return;
    setErr('');
    setMsg('');
    const nextValue = String(value || '').trim();
    if (!nextValue) {
      setErr(isPhone ? t('account.profile.otp.phoneRequired') : t('account.profile.otp.emailRequired'));
      return;
    }
    setLoading(true);
    try {
      let nextChallenge = challenge;
      if (!nextChallenge || step === 'input') {
        const started = await api.startContactChange(token, { type, value: nextValue });
        nextChallenge = started.challenge;
        setChallenge(nextChallenge);
      }
      const sent = await api.sendContactCode(token, { challengeId: nextChallenge.id });
      setStep('code');
      setMsg(t('account.profile.otp.codeSent'));
      startCooldown(Number(sent?.retryAfter) || OTP_RESEND_COOLDOWN_SEC);
    } catch (error) {
      const retryAfter = Number(error?.meta?.retryAfter || error?.retryAfter);
      if (retryAfter > 0) startCooldown(retryAfter);
      setErr(error.message);
    } finally {
      setLoading(false);
    }
  };

  const verify = async (e) => {
    e?.preventDefault?.();
    setErr('');
    setMsg('');
    if (!challenge?.id) {
      setErr(t('account.profile.otp.sessionMissing'));
      return;
    }
    if (!String(code || '').trim()) {
      setErr(t('account.profile.otp.codeRequired'));
      return;
    }
    setLoading(true);
    try {
      const res = await api.verifyContactChange(token, { challengeId: challenge.id, code });
      setMsg(t('account.profile.otp.saved'));
      onDone?.(res);
    } catch (error) {
      setErr(error.message);
    } finally {
      setLoading(false);
    }
  };

  const resendLabel = step === 'code' && !canResend
    ? t('account.profile.otp.resendIn', { seconds: secondsLeft })
    : t('account.profile.otp.resend');

  return (
    <ProfileChangeContact
      variant={isPhone ? 'phone' : 'email'}
      title={title}
      message={step === 'code' ? codeMessage : inputMessage}
      valueLabel={isPhone ? t('account.profile.field.phone').replace(/:$/, '') : t('account.profile.field.email').replace(/:$/, '')}
      codeLabel={t('account.profile.otp.codeLabel')}
      value={value}
      code={code}
      step={step}
      maskedContact={challenge?.maskedContact || ''}
      onValueChange={setValue}
      onCodeChange={setCode}
      onSendCode={sendCode}
      onVerify={verify}
      onBack={() => {
        setStep('input');
        setCode('');
        setErr('');
        setMsg('');
        setChallenge(null);
        resetCooldown();
      }}
      loading={loading}
      resendDisabled={step === 'code' && !canResend}
      sendLabel={t('auth.verify.getCode')}
      confirmLabel={t('auth.verify.confirm')}
      resendLabel={resendLabel}
      backLabel={t('auth.verify.cancel')}
      error={err}
      success={msg}
    />
  );
}
